import {
  ASSETS,
  ContentButton,
  ContentListGrid,
  ContentExpandedListGrid,
  EventPopupButtonFields,
  SetsContentSelection,
} from "./fragments";

export const PING = `
  query {
    ping
  }
`;

/** Optional — staging CMS may not have this field until the rebrand branch is deployed */
export const FOOTER_ROSE_DATA = `
  query {
    globalSet(handle: "settings") {
      ... on GlobalSet_Settings {
        footer_rose {
          ${ASSETS}
        }
      }
    }
  }
`;

/** Soft-fetched separately so missing CMS field does not break SETTINGS_DATA. */
export const MAP_ENABLED_SETTING = `
  query {
    globalSet(handle: "settings") {
      ... on GlobalSet_Settings {
        enable_map
      }
    }
  }
`;

export const SETTINGS_DATA = `
  query {
    globalSet(handle: "settings") {
      ... on GlobalSet_Settings {
        enable_show_mode
        file {
          ${ASSETS}
        }
        current_theme {
          ... on Entry_Themes_Theme {
            id
            background_color
            foreground_color
            button_background_color
            button_foreground_color
            card_background_color
            card_foreground_color
            header_background_color
            header_foreground_color
            highlights_foreground_color
            highlights_background_color
            card_border_radius
            card_border_width
            button_border_radius
            button_border_width
            top_left_overlay_image {
              ${ASSETS}
            }
            top_right_overlay_image {
              ${ASSETS}
            }
            bottom_left_overlay_image {
              ${ASSETS}
            }
            bottom_right_overlay_image {
              ${ASSETS}
            }
            bottom_left_width
            bottom_right_width
            top_left_width
            top_right_width
          }
        }
        main_logo {
          ${ASSETS}
        }
        show_search
        show_search_icon
        show_buy
        buy_tickets_link { url title }
        show_mode_main_nav {
          tree {
            page {
              url
              title
              entry_id
              id
              ... on NavEntryPage_MainNavShowMode_Pages_Default {
                action {
                  ... on Term_UiElements_UiElement {
                    id
                    permalink
                    slug
                    title
                    url
                  }
                }
                description
                heading
                icon {
                  label
                  value
                }
              }
              ... on NavBasicPage_MainNavShowMode {
                id
                action {
                  ... on Term_UiElements_UiElement {
                    slug
                    title
                    id
                  }
                }
                description
                heading
                icon {
                  value
                  label
                }
              }
            }
            children {
              page {
                ... on NavEntryPage_MainNavShowMode_Pages_Default {
                  description
                  heading
                  action {
                    ... on Term_UiElements_UiElement {
                      id
                      slug
                      title
                      url
                      permalink
                    }
                  }
                  icon {
                    value
                    label
                  }
                }
                title
                url
                entry_id
                id
                ... on NavBasicPage_MainNavShowMode {
                  id
                  description
                  heading
                  icon {
                    value
                    label
                  }
                }
              }
            }
          }
        }
        main_footer_logo {
          ${ASSETS}
        }
        name
        phone
        sub_logo {
          ${ASSETS}
        }
        socials {
          link { url title }
          social_type {
            value
            label
          }
        }
        content_right
        content_bottom
        address
        email
        show_mode_footer_nav {
          tree {
            page {
              url
              title
              id
              entry_id
            }
            children {
              page {
                url
                title
                id
                entry_id
              }
            }
          }
        }
        out_of_show_mode_footer_nav {
          tree {
            page {
              id
              title
              url
              entry_id
            }
            children {
              page {
                url
                title
                id
                entry_id
              }
            }
          }
        }
        out_of_show_mode_main_nav {
          tree {
            page {
              ... on NavEntryPage_MainNavOutOfShow_Pages_Default {
                action {
                  ... on Term_UiElements_UiElement {
                    id
                    permalink
                    slug
                    title
                    url
                  }
                }
                description
                heading
                icon {
                  label
                  value
                }
              }
              title
              url
              entry_id
              ... on NavBasicPage_MainNavOutOfShow {
                id
                action {
                  ... on Term_UiElements_UiElement {
                    id
                    slug
                    title
                  }
                }
                description
                heading
                icon {
                  value
                  label
                }
              }
            }
            children {
              page {
                ... on NavEntryPage_MainNavOutOfShow_Pages_Default {
                  action {
                    ... on Term_UiElements_UiElement {
                      id
                      url
                      title
                      permalink
                    }
                  }
                  description
                  heading
                  icon {
                    value
                    label
                  }
                }
                url
                title
                id
                entry_id
                ... on NavBasicPage_MainNavOutOfShow {
                  id
                  action {
                    ... on Term_UiElements_UiElement {
                      id
                      slug
                      title
                    }
                  }
                  description
                  heading
                  icon {
                    label
                    value
                  }
                }
              }
            }
          }
        }
        show_banner
        banner {
          ... on Entry_Banners_Banner {
            id
            title_text_color
            background_color
            title
            link { url title }
            content
          }
        }
      }
    }
  }
`;

export const SHOWBAGS_DATA = `
  query {
    entries(sort: "title", collection: "showbags") {
      data {
        ... on Entry_Showbags_Showbag {
          id
          title
          show_mode_only
          url
          show_as_page
          categories {
            ... on Term_Locations_Locations {
              slug
              title
            }
            ... on Term_ShowbagCategories_ShowbagCategory {
              slug
              title
            }
            ... on Term_ShowbagOperators_ShowbagOperator {
              slug
              title
            }
            ... on Term_ShowbagStalls_ShowbagStall {
              slug
              title
            }
          }
          image {
            ${ASSETS}
          }
          showbag_price
          showbag_value
          slug
          featured
          items {
            id
            item_id
            item_quantity
            item_title
          }
          showbag_id
          showbag_operator {
            ... on Term_ShowbagOperators_ShowbagOperator {
              id
              author {
                name
              }
              content
              title
            }
          }
          showbag_stalls {
            ... on Term_ShowbagStalls_ShowbagStall {
              id
              title
            }
          }
          locations {
            ... on Term_Locations_Locations {
              id
              description
              title
              url
              slug
              long
              lat
              categories: location_categories {
                ... on Term_LocationCategories_LocationCategory {
                  title
                  icon
                  slug
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const USER_DATA = `
  query ($id: String!){
    user(id: $id) {
      my_showbags {
        ... on Entry_Showbags_Showbag {
          id
          title
          categories {
            ... on Term_ShowbagCategories_ShowbagCategory {
              slug
              title
            }
            ... on Term_Locations_Locations {
              title
              slug
            }
            ... on Term_ShowbagOperators_ShowbagOperator {
              slug
              title
            }
            ... on Term_ShowbagStalls_ShowbagStall {
              slug
              title
            }
          }
          showbag_price
          location {
            ... on Term_Locations_Locations {
              id
              title
              url
              permalink
              slug
            }
          }
          image {
            ${ASSETS}
          }
          slug
        }
      }
    }
  }
`;

export const LOCATIONS_DATA = `
  query {
    terms(taxonomy: "locations") {
      data {
        ... on Term_Locations_Locations {
          id
          title
          slug
          icon
          description
          categories: location_categories {
            ... on Term_LocationCategories_LocationCategory {
              title
              icon
              slug
            }
          }
          long
          lat
          events {
            ... on Entry_Events_Event {
              id
              all_day
              start_time
              start_date
              end_time
              end_date
              every_day
              hide_end_time
              hide_times_on_card
              multi_day
              days {
                all_day_multi_day
                date
                end_time
                start_time
                hide_end_time
              }
              categories {
                ... on Term_EventCategories_EventCategory {
                  id
                  title
                  url
                  slug
                }
              }
              slideshow_images {
                ${ASSETS}
              }
              recurrence {
                label
                value
              }
              title
              url
              event_type {
                ... on Term_EventType_EventType {
                  id
                  url
                  title
                  slug
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const FACILITIES_DATA = `
query {
  terms(taxonomy: "facilities") {
    data {
      ... on Term_Facilities_Facility {
        id
        description
        categories: location_categories {
          ... on Term_LocationCategories_LocationCategory {
            title
            icon
            slug
          }
        }
        long
        lat
        permalink
        slug
        icon
        title
        url
      }
    }
  }
}
`;

export const RIDES_DATA = `
  query {
    entries(sort: "title", collection: "rides") {
      data {
        ... on Entry_Rides_Ride {
          show_as_page
          id
          title
          show_mode_only
          url
          categories {
            ... on Term_RideCategories_RideCategory {
              id
              title
              slug
            }
          }
          locations {
            ... on Term_Locations_Locations {
              id
              description
              title
              url
              slug
              long
              lat
              categories: location_categories {
                ... on Term_LocationCategories_LocationCategory {
                  title
                  icon
                  slug
                }
              }
            }
          }
          slideshow_images {
            ${ASSETS}
          }
          grid_image {
            ${ASSETS}
          }
          slug
          content {
            ${SetsContentSelection}
          }
          min_height
          price
        }
      }
    }
  }
`;

export const FOOD_DATA = `
  query {
    entries(sort: "title", collection: "food_outlets") {
      data {
        ... on Entry_FoodOutlets_FoodOutlet {
          id
          categories {
            ... on Term_FoodCategories_FoodCategory {
              id
              slug
              title
            }
          }
          slideshow_images {
            ${ASSETS}
          }
          grid_image {
            ${ASSETS}
          }
          title
          show_mode_only
          slug
          url
          content {
            ${SetsContentSelection}
          }
          food_type {
            title
            url
            slug
          }
          locations {
            ... on Term_Locations_Locations {
              id
              description
              categories: location_categories {
                ... on Term_LocationCategories_LocationCategory {
                  title
                  icon
                  slug
                }
              }
              url
              slug
              title
            }
          }
          show_as_page
        }
      }
    }
  }
`;

export const ALL_EVENTS_DATA = `
  query {
    entries(collection: "events") {
      data {
        id
        ... on Entry_Events_Event {
          slug
          recurrence {
            value
            label
          }
          show_as_page
          locale
          all_day
          categories {
            ... on Term_EventCategories_EventCategory {
              id
              title
              slug
              url
              permalink
            }
          }
          title
          show_mode_only
          url
          permalink
          status
          short_description
          popup_buttons {
            ${EventPopupButtonFields}
          }
          start_time
          start_date
          every_day
          multi_day
          hide_times_on_card
          locations {
            ... on Term_Locations_Locations {
              id
              title
              url
              permalink
              slug
            }
          }
          slideshow_images {
            ${ASSETS}
          }
          grid_image {
            ${ASSETS}
          }
          end_time
          end_date
          days {
            date
            end_time
            all_day_multi_day
            id
            start_time
            hide_end_time
            override_display_data
            override_grid {
              ${ASSETS}
            }
            override_short
            override_title
          }
          date
          content {
            ${SetsContentSelection}
          }
          event_type {
            ... on Term_EventType_EventType {
              id
              slug
              title
              url
            }
          }
          competition {
            ... on Entry_Competitions_Competition {
              id
              title
            }
          }
          hide_end_time
          tags {
            ... on Term_Tags_Tag {
              id
              edit_url
              permalink
              slug
              taxonomy {
                handle
                title
              }
              title
              uri
              url
            }
          }
        }
      }
    }
  }
`;

export const SHOW_DATES = `
  query {
    globalSet(handle: "settings") {
      ... on GlobalSet_Settings {
        show_dates {
          end
          start
        }
        show_start_time
        test_mode
        test_date
        show_count
      }
    }
  }
`;

export const COMPETITIONS_DATA = `
  query {
    entries(sort: "title", collection: "competitions") {
      data {
        ... on Entry_Competitions_Competition {
          id
          content {
            ${SetsContentSelection}
          }
          title
          slug
          show_mode_only
          url
          show_as_page
          link_to_external_site
          external_link
          locations {
            ... on Term_Locations_Locations {
              id
              description
              url
              permalink
              slug
              categories: location_categories {
                ... on Term_LocationCategories_LocationCategory {
                  title
                  icon
                  slug
                }
              }
              title
              lat
              long
            }
          }
          grid_image {
            ${ASSETS}
          }
          slideshow_images {
            ${ASSETS}
          }
          entries_open
          entries_close
          late_entries_close
          entries_link
          categories {
            ... on Term_CompetitionCategories_CompetitionCategory {
              id
              title
              slug
              url
              permalink
            }
          }
          tags {
            ... on Term_Tags_Tag {
              id
              edit_url
              permalink
              slug
              taxonomy {
                title
                handle
              }
              title
              uri
              url
            }
          }
        }
      }
    }
  }
`;

export const COMMERCIAL_EXHIBITORS = `
  query {
    entries(sort: "title", collection: "commercial_exhibitors") {
      data {
        ... on Entry_CommercialExhibitors_CommercialExhibitor {
          id
          company_email
          exhibitor_types {
            ... on Term_ExhibitorType_ExhibitorType {
              id
              title
              slug
              url
            }
          }
          facebook
          instagram
          slideshow_images {
            ${ASSETS}
          }
          grid_image {
            ${ASSETS}
          }
          website
          discount_code
          title
          url
          slug
          show_mode_only
          show_as_page
          listing_tier {
            label
            value
          }
          locations {
            ... on Term_Locations_Locations {
              id
              title
              url
              permalink
              long
              slug
              lat
              categories: location_categories {
                ... on Term_LocationCategories_LocationCategory {
                  title
                  icon
                  slug
                }
              }
              description
            }
          }
          content {
            ${SetsContentSelection}
          }
        }
      }
    }
  }
`;

export const ALL_NEWS_DATA = `
  query {
    entries(sort: "date desc", collection: "news") {
      data {
        ... on Entry_News_News {
          id
          grid_image {
            ${ASSETS}
          }
          title
          show_mode_only
          show_as_page
          date
          author {
            name
          }
          url
          slug
        }
      }
    }
  }
`;

export const SITEMAP = `
  query {
    entries {
      data {
        ... on Entry_News_News {
          id
          url
          title
          show_mode_only
          permalink
          last_modified(format: "c")
        }
        ... on Entry_CommercialExhibitors_CommercialExhibitor {
          id
          url
          title
          show_mode_only
          permalink
          last_modified(format: "c")
        }
        ... on Entry_Events_Event {
          id
          url
          title
          show_mode_only
          permalink
          last_modified(format: "c")
        }
        ... on Entry_Showbags_Showbag {
          id
          url
          title
          show_mode_only
          permalink
          last_modified(format: "c")
        }
        ... on Entry_Rides_Ride {
          id
          url
          title
          show_mode_only
          permalink
          last_modified(format: "c")
        }
        ... on Entry_FoodOutlets_FoodOutlet {
          id
          url
          title
          show_mode_only
          permalink
          last_modified(format: "c")
        }
        ... on Entry_Competitions_Competition {
          id
          url
          title
          show_mode_only
          permalink
          last_modified(format: "c")
        }
        ... on Entry_Pages_Default {
          id
          title
          url
          show_mode_only
          permalink
          last_modified
        }
      }
    }
  }
`;

export const MYSHOW_ENTRIES = `
  query ($slugs: [String!]!) {
    events: entries(collection: "events", filter: {slug: {in: $slugs}}) {
      data {
        ... on Entry_Events_Event {
          id
          title
          slug
          start_date
          end_date
          end_time
          multi_day
          hide_times_on_card
          recurrence {
            value
            label
          }
          start_date
          start_time
          short_description
          popup_buttons {
            ${EventPopupButtonFields}
          }
          content {
            ${SetsContentSelection}
          }
          date
          every_day
          days {
            all_day_multi_day
            date
            end_time
            start_time
            id
            override_title
            override_short
            override_display_data
            override_grid {
              ${ASSETS}
            }
          }
          categories {
            ... on Term_EventCategories_EventCategory {
              id
              slug
              title
            }
          }
          locations {
            ... on Term_Locations_Locations {
              id
              title
              url
              permalink
              slug
            }
          }
          event_type {
            ... on Term_EventType_EventType {
              id
              title
              url
              permalink
            }
          }
          grid_image {
            ... on Asset_Assets {
              ${ASSETS}
            }
          }
        }
      }
    }
    showbags: entries(collection: "showbags", filter: {slug: {in: $slugs}}) {
      data {
        ... on Entry_Showbags_Showbag {
          id
          title
          slug
          show_mode_only
          blueprint
          showbag_price
          showbag_value
          image {
            ... on Asset_Assets {
              ${ASSETS}
            }
          }
          slideshow_images {
            ... on Asset_Assets {
              ${ASSETS}
            }
          }
          showbag_operator {
            title
            url
            slug
          }
          items {
            id
            item_id
            item_quantity
            item_title
          }
          categories {
            ... on Term_ShowbagCategories_ShowbagCategory {
              id
              slug
              title
            }
          }
          showbag_stalls {
            ... on Term_ShowbagStalls_ShowbagStall {
              id
              title
            }
          }
          locations {
            ... on Term_Locations_Locations {
              id
              description
              title
              url
              slug
              long
              lat
              categories: location_categories {
                ... on Term_LocationCategories_LocationCategory {
                  title
                  icon
                  slug
                }
              }
            }
          }
        }
      }
    }
    food: entries(collection: "food_outlets", filter: {slug: {in: $slugs}}) {
      data {
        ... on Entry_FoodOutlets_FoodOutlet {
          id
          title
          slug
          content {
            ${SetsContentSelection}
          }
          locations {
            ... on Term_Locations_Locations {
              id
              title
              url
              permalink
              slug
            }
          }
          categories {
            ... on Term_FoodCategories_FoodCategory {
              id
              slug
              title
            }
          }
          grid_image {
            ... on Asset_Assets {
              ${ASSETS}
            }
          }
        }
      }
    }
    rides: entries(collection: "rides", filter: {slug: {in: $slugs}}) {
      data {
        ... on Entry_Rides_Ride {
          id
          title
          slug
          min_height
          price
          categories {
            ... on Term_RideCategories_RideCategory {
              id
              title
              slug
            }
          }
          locations {
            ... on Term_Locations_Locations {
              id
              description
              title
              url
              slug
              long
              lat
              categories: location_categories {
                ... on Term_LocationCategories_LocationCategory {
                  title
                  icon
                  slug
                }
              }
            }
          }
          content {
            ${SetsContentSelection}
          }
          grid_image {
            ... on Asset_Assets {
              ${ASSETS}
            }
          }
        }
      }
    }
  }
`;
